import { BusinessAnalytics } from "@xepp/business-analytics-sdk";
import { maskEmail, maskPhoneNumber } from "../../pages/customer/onboarding/forgot-password/util";
import { withoutEmpty } from "./customFields";

export { describeError } from "./customFields";

/**
 * Login, OTP, password reset and logout events for the web app, mirroring the shared
 * analytics event sheet (categories "Regular Login", "Login with OTP", "Password Reset"
 * and "Logout").
 *
 * Every flow and step code has to exist in the analytics catalog (FlowDefinitions /
 * FlowSteps) — ingestion accepts unknown codes over HTTP and then silently discards
 * them — so the codes live here and nowhere else. The flow codes are the ones the
 * backend seeds in SeedProductAnalyticsCatalog, not the sheet's category names: the
 * catalog calls "Regular Login" login_password and "Login with OTP" login_otp.
 */
export const AUTH_FLOWS = {
	regularLogin: "login_password",
	loginWithOtp: "login_otp",
	passwordReset: "password_reset",
	logout: "logout",
} as const;

export const AUTH_EVENTS = {
	loginStarted: "login_started",
	loginSuccess: "login_success",
	loginFailed: "login_failed",
	loginOtpSent: "login_otp_sent",
	loginOtpResent: "login_otp_resent",
	passwordResetSuccess: "password_reset_success",
	passwordResetFailed: "password_reset_failed",
	logout: "logout",
} as const;

/** How the user is signing in: email + password, or a one-time code sent to them. */
export type LoginMethod = "password" | "otp";

export type OtpChannel = "email" | "sms";

const flowFor = (method: LoginMethod) => (method === "otp" ? AUTH_FLOWS.loginWithOtp : AUTH_FLOWS.regularLogin);

export const trackLoginStarted = (method: LoginMethod): void => {
	BusinessAnalytics.track(AUTH_EVENTS.loginStarted, flowFor(method), { method });
};

export const trackLoginSuccess = (method: LoginMethod, userId: string | null): void => {
	BusinessAnalytics.track(AUTH_EVENTS.loginSuccess, flowFor(method), withoutEmpty({ method, user_id: userId }));
};

export const trackLoginFailed = (method: LoginMethod, errorReason: string): void => {
	BusinessAnalytics.track(AUTH_EVENTS.loginFailed, flowFor(method), withoutEmpty({ method, error_reason: errorReason }));
};

/** `contact` is the raw email address or phone number; only its masked form is sent. */
export const trackLoginOtpSent = (channel: OtpChannel, contact: string, dialCode?: string): void => {
	const masked = channel === "email" ? { email_masked: maskEmail(contact) } : { phone_masked: maskPhoneNumber(contact, dialCode) };
	BusinessAnalytics.track(AUTH_EVENTS.loginOtpSent, AUTH_FLOWS.loginWithOtp, withoutEmpty({ channel, ...masked }));
};

export const trackLoginOtpResent = (channel: OtpChannel, resendCount: number): void => {
	BusinessAnalytics.track(AUTH_EVENTS.loginOtpResent, AUTH_FLOWS.loginWithOtp, { channel, resend_count: resendCount });
};

/** `channel` is how the reset code or link reached the user — always email on the web today. */
export const trackPasswordResetSuccess = (channel: OtpChannel): void => {
	BusinessAnalytics.track(AUTH_EVENTS.passwordResetSuccess, AUTH_FLOWS.passwordReset, { channel });
};

export const trackPasswordResetFailed = (channel: OtpChannel, errorReason: string): void => {
	BusinessAnalytics.track(AUTH_EVENTS.passwordResetFailed, AUTH_FLOWS.passwordReset, withoutEmpty({ channel, error_reason: errorReason }));
};

export const trackLogout = (userId: string | null, sessionDurationS: number | null): void => {
	BusinessAnalytics.track(AUTH_EVENTS.logout, AUTH_FLOWS.logout, withoutEmpty({ user_id: userId, session_duration_s: sessionDurationS }));
};
